import { type ReactNode, useId } from 'react';

/**
 * Shop and league illustrations (original artwork): green chance dice in a slot machine, a bowl or a sack, hearts
 * in a barrel, a pot, a sack or a box, the bunny, the VIP crown, the invite card, the league cups and the boosters.
 * Each picture is one SVG in a 100×100 box with its own gradients.
 */

/** Gradient ids of one picture (each picture gets its own, so pictures never share defs). */
interface Paint {
  heart: string;
  dieTop: string;
  dieLeft: string;
  dieRight: string;
  gold: string;
  red: string;
  purple: string;
  wood: string;
}

function Defs({ id }: { id: string }) {
  return (
    <defs>
      <radialGradient id={`${id}heart`} cx="0.38" cy="0.3" r="0.75">
        <stop offset="0" stopColor="#ff8456" />
        <stop offset="0.55" stopColor="#f6491f" />
        <stop offset="1" stopColor="#d22d10" />
      </radialGradient>
      <linearGradient id={`${id}dieTop`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#9af5cb" />
        <stop offset="1" stopColor="#4fd598" />
      </linearGradient>
      <linearGradient id={`${id}dieLeft`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#36c189" />
        <stop offset="1" stopColor="#1f9466" />
      </linearGradient>
      <linearGradient id={`${id}dieRight`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#1f9a68" />
        <stop offset="1" stopColor="#0f6e47" />
      </linearGradient>
      <linearGradient id={`${id}gold`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#ffe48a" />
        <stop offset="0.5" stopColor="#f5b52a" />
        <stop offset="1" stopColor="#c98112" />
      </linearGradient>
      <linearGradient id={`${id}red`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#ff6a55" />
        <stop offset="0.6" stopColor="#e22d22" />
        <stop offset="1" stopColor="#a8160f" />
      </linearGradient>
      <linearGradient id={`${id}purple`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#b493ff" />
        <stop offset="0.6" stopColor="#7a4fdc" />
        <stop offset="1" stopColor="#4c2a9e" />
      </linearGradient>
      <linearGradient id={`${id}wood`} x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stopColor="#7a4318" />
        <stop offset="0.45" stopColor="#c8853f" />
        <stop offset="1" stopColor="#6e3a12" />
      </linearGradient>
    </defs>
  );
}

/** A picture: the SVG box with its gradients; `draw` gets the gradient fills. */
function Art({ className, draw }: { className?: string; draw: (p: Paint) => ReactNode }) {
  const id = useId().replace(/:/g, '');
  const url = (name: string) => `url(#${id}${name})`;
  const paint: Paint = {
    heart: url('heart'),
    dieTop: url('dieTop'),
    dieLeft: url('dieLeft'),
    dieRight: url('dieRight'),
    gold: url('gold'),
    red: url('red'),
    purple: url('purple'),
    wood: url('wood'),
  };
  return (
    <svg className={className ?? 'pack-svg'} viewBox="0 0 100 100" aria-hidden>
      <Defs id={id} />
      <g style={{ filter: 'drop-shadow(0 1.5px 1.5px rgba(70, 30, 0, 0.28))' }}>{draw(paint)}</g>
    </svg>
  );
}

/* ---- parts --------------------------------------------------------------------------------------------- */

const HEART =
  'M0 15C-4 12.2-16 5.6-16-4.2-16-10.6-11.6-14-7.2-14-3.6-14-1.2-11.8 0-9.2 1.2-11.8 3.6-14 7.2-14 11.6-14 16-10.6 16-4.2 16 5.6 4 12.2 0 15Z';

/** [x, y, width, turn] */
type HeartAt = [number, number, number, number];

/** A glossy heart centred on (x, y), `size` wide, turned by `turn` degrees. */
export function Heart({ at: [x, y, size, turn], fill }: { at: HeartAt; fill: string }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${turn}) scale(${size / 32})`}>
      <path d={HEART} fill={fill} />
      <path
        d="M-12-6C-12-10-8.6-11.6-6-11-3.6-10.4-3.4-8-5.6-7-8-6-9.8-4.4-10.6-2.6-11.4-1-12-3-12-6Z"
        fill="#fff"
        opacity="0.4"
      />
    </g>
  );
}

function Hearts({ list, p }: { list: HeartAt[]; p: Paint }) {
  return (
    <>
      {list.map((at, i) => (
        <Heart key={i} at={at} fill={p.heart} />
      ))}
    </>
  );
}

/** Pips of a die face in its unit square. */
const PIPS: Record<number, [number, number][]> = {
  1: [[0.5, 0.5]],
  2: [
    [0.28, 0.28],
    [0.72, 0.72],
  ],
  3: [
    [0.25, 0.25],
    [0.5, 0.5],
    [0.75, 0.75],
  ],
};

function Face({ matrix, fill, pips }: { matrix: string; fill: string; pips: number }) {
  return (
    <g transform={`matrix(${matrix})`}>
      <rect width="1" height="1" rx="0.14" fill={fill} />
      {PIPS[pips]!.map(([cx, cy]) => (
        <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="0.1" fill="#f2fff8" />
      ))}
    </g>
  );
}

/** [x, y, size]: (x, y) is the near top corner of the die. */
type DieAt = [number, number, number];

/** A green chance die seen from above a corner: the top face and two sides. */
export function Die({ at: [x, y, s], p }: { at: DieAt; p: Paint }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      <Face matrix="0.5 -0.25 0.5 0.25 -0.5 -0.25" fill={p.dieTop} pips={1} />
      <Face matrix="0.5 0.25 0 0.56 -0.5 -0.25" fill={p.dieLeft} pips={3} />
      <Face matrix="0.5 -0.25 0 0.56 0 0" fill={p.dieRight} pips={2} />
    </g>
  );
}

function Dice({ list, p }: { list: DieAt[]; p: Paint }) {
  return (
    <>
      {[...list]
        .sort((a, b) => a[1] - b[1])
        .map((at, i) => (
          <Die key={i} at={at} p={p} />
        ))}
    </>
  );
}

/** A red sack tied with gold; what it holds is drawn in its mouth before the body. */
function Sack({ p, children }: { p: Paint; children: ReactNode }) {
  return (
    <>
      <ellipse cx="50" cy="44" rx="19" ry="5" fill="#7d0f0b" />
      {children}
      <path d="M31 46C16 58 14 90 50 91 86 90 84 58 69 46Z" fill={p.red} />
      <path
        d="M34 50C26 60 26 74 30 80"
        stroke="#fff"
        strokeWidth="3"
        strokeLinecap="round"
        opacity="0.3"
        fill="none"
      />
      <path d="M31 44Q50 52 69 44L68 50Q50 58 32 50Z" fill={p.gold} stroke="#a86a0c" strokeWidth="0.6" />
    </>
  );
}

/* ---- chance packs ------------------------------------------------------------------------------------ */

/** The art of a chance pack: dice in a slot machine (300), a bowl (50), a sack (15), or loose dice (5, 1). */
export function ChanceArt({ chances }: { chances: number }) {
  if (chances >= 300)
    return (
      <Art
        draw={(p) => (
          <>
            <Dice
              list={[
                [24, 36, 17],
                [40, 31, 17],
                [56, 31, 17],
                [72, 36, 17],
                [32, 41, 17],
                [48, 38, 17],
                [64, 41, 17],
              ]}
              p={p}
            />
            <rect
              x="13"
              y="47"
              width="74"
              height="9"
              rx="4"
              fill={p.gold}
              stroke="#a86a0c"
              strokeWidth="0.8"
            />
            <rect
              x="17"
              y="54"
              width="66"
              height="34"
              rx="5"
              fill={p.red}
              stroke="#a86a0c"
              strokeWidth="1.2"
            />
            {[22, 40, 58].map((x) => (
              <g key={x}>
                <rect
                  x={x}
                  y="60"
                  width="18"
                  height="18"
                  rx="3"
                  fill="#fff7e0"
                  stroke="#c98112"
                  strokeWidth="1.4"
                />
                <text x={x + 9} y="74" textAnchor="middle" fontSize="14" fontWeight="900" fill="#e22d22">
                  7
                </text>
              </g>
            ))}
            <rect x="21" y="86" width="58" height="7" rx="2" fill="#8a1414" />
            <path d="M84 66 90 50" stroke="#c9ced6" strokeWidth="3" strokeLinecap="round" />
            <circle cx="90.5" cy="48" r="4.5" fill={p.red} />
            <Die at={[80, 76, 19]} p={p} />
          </>
        )}
      />
    );
  if (chances >= 50)
    return (
      <Art
        draw={(p) => (
          <>
            <ellipse cx="50" cy="50" rx="36" ry="7" fill="#7a4410" />
            <Dice
              list={[
                [32, 42, 17],
                [48, 38, 17],
                [64, 42, 17],
                [40, 47, 17],
                [56, 47, 17],
              ]}
              p={p}
            />
            <path d="M14 50A36 7 0 0 0 86 50C86 74 72 88 50 88 28 88 14 74 14 50Z" fill={p.gold} />
            <path d="M14 50A36 7 0 0 0 86 50" stroke="#ffe9a6" strokeWidth="2" fill="none" />
            <path
              d="M14 50C14 74 28 88 50 88 72 88 86 74 86 50"
              stroke="#a86a0c"
              strokeWidth="1"
              fill="none"
            />
            <g transform="translate(50 69) scale(0.55)">
              <path d={HEART} fill={p.red} stroke="#fff3c4" strokeWidth="2.4" />
            </g>
            <Die at={[80, 76, 19]} p={p} />
          </>
        )}
      />
    );
  if (chances >= 15)
    return (
      <Art
        draw={(p) => (
          <>
            <Sack p={p}>
              <Dice
                list={[
                  [42, 32, 15],
                  [58, 32, 15],
                  [50, 28, 15],
                ]}
                p={p}
              />
            </Sack>
            <Die at={[80, 76, 18]} p={p} />
          </>
        )}
      />
    );
  if (chances >= 5)
    return (
      <Art
        draw={(p) => (
          <Dice
            list={[
              [36, 52, 26],
              [64, 52, 26],
              [50, 34, 26],
            ]}
            p={p}
          />
        )}
      />
    );
  return (
    <Art
      draw={(p) => (
        <Dice
          list={[
            [37, 42, 28],
            [63, 46, 28],
          ]}
          p={p}
        />
      )}
    />
  );
}

/* ---- heart packs --------------------------------------------------------------------------------------- */

const HEAP: HeartAt[] = [
  [34, 38, 26, -16],
  [66, 39, 24, 14],
  [50, 30, 28, 4],
  [43, 46, 20, -4],
  [58, 47, 18, 10],
];

/** 12500: a wooden barrel brimming with hearts. */
function Barrel() {
  return (
    <Art
      draw={(p) => (
        <>
          <ellipse cx="50" cy="48" rx="26" ry="5" fill="#4a2408" />
          <Hearts list={HEAP} p={p} />
          <path d="M24 48A26 5 0 0 0 76 48C81 62 81 80 74 93H26C19 80 19 62 24 48Z" fill={p.wood} />
          {[36, 50, 64].map((x) => (
            <path key={x} d={`M${x} 53V93`} stroke="rgba(60, 28, 6, 0.35)" strokeWidth="1" />
          ))}
          <path d="M22 58Q50 64 78 58" stroke={p.gold} strokeWidth="4" fill="none" />
          <path d="M22 84Q50 90 78 84" stroke={p.gold} strokeWidth="4" fill="none" />
          <path d="M24 48A26 5 0 0 0 76 48" stroke="#e6a54e" strokeWidth="2" fill="none" />
          <Heart at={[80, 84, 16, 16]} fill={p.heart} />
        </>
      )}
    />
  );
}

/** 6000: a purple pot of hearts with a gold rim. */
function Pot() {
  return (
    <Art
      draw={(p) => (
        <>
          <ellipse cx="50" cy="52" rx="30" ry="6" fill="#2e1566" />
          <Hearts list={HEAP.map(([x, y, s, r]) => [x, y + 4, s, r] as HeartAt)} p={p} />
          <path d="M20 52A30 6 0 0 0 80 52C85 72 74 89 50 89 26 89 15 72 20 52Z" fill={p.purple} />
          <path d="M20 52A30 6 0 0 0 80 52" stroke={p.gold} strokeWidth="4.5" fill="none" />
          <path
            d="M27 62C25 70 28 78 33 82"
            stroke="#fff"
            strokeWidth="3"
            strokeLinecap="round"
            opacity="0.3"
            fill="none"
          />
          <path
            d="M18 56C10 56 10 66 19 66M82 56C90 56 90 66 81 66"
            stroke={p.gold}
            strokeWidth="3"
            fill="none"
          />
          <Heart at={[80, 84, 15, 14]} fill={p.heart} />
        </>
      )}
    />
  );
}

/** 2200: a red sack with hearts. */
function HeartSack() {
  return (
    <Art
      draw={(p) => (
        <>
          <Sack p={p}>
            <Hearts
              list={[
                [42, 34, 18, -14],
                [58, 34, 18, 12],
                [50, 29, 20, 0],
              ]}
              p={p}
            />
          </Sack>
          <Heart at={[80, 84, 15, 14]} fill={p.heart} />
        </>
      )}
    />
  );
}

/** 500: an open gold box with hearts. */
function HeartBox() {
  return (
    <Art
      draw={(p) => (
        <>
          <path d="M18 50 26 34 82 30 84 50Z" fill="#ffd36a" stroke="#c98112" strokeWidth="1" />
          <Hearts
            list={[
              [38, 46, 20, -14],
              [62, 46, 18, 14],
              [50, 40, 22, 2],
            ]}
            p={p}
          />
          <rect x="18" y="50" width="66" height="38" rx="3" fill={p.gold} stroke="#a86a0c" strokeWidth="1" />
          <rect x="46" y="50" width="10" height="38" fill={p.red} />
          <rect x="18" y="50" width="66" height="6" fill="#fff" opacity="0.22" />
          <Heart at={[82, 84, 15, 14]} fill={p.heart} />
        </>
      )}
    />
  );
}

/** 10: three hearts. */
function HeartTrio() {
  return (
    <Art
      draw={(p) => (
        <Hearts
          list={[
            [32, 60, 30, -16],
            [68, 60, 30, 16],
            [50, 50, 38, 0],
          ]}
          p={p}
        />
      )}
    />
  );
}

/** 20: a white bunny hugging a heart. */
function Bunny() {
  return (
    <Art
      draw={(p) => (
        <>
          {[-1, 1].map((k) => (
            <g key={k} transform={`rotate(${k * 12} ${50 + k * 9} 40)`}>
              <ellipse
                cx={50 + k * 9}
                cy="24"
                rx="6.5"
                ry="17"
                fill="#e9e1d9"
                stroke="#c4b8ac"
                strokeWidth="1"
              />
              <ellipse cx={50 + k * 9} cy="25" rx="3.2" ry="12" fill="#ffb6c8" />
            </g>
          ))}
          <ellipse cx="50" cy="80" rx="19" ry="14" fill="#e9e1d9" stroke="#c4b8ac" strokeWidth="1" />
          <circle cx="50" cy="52" r="20" fill="#f1ebe5" stroke="#c4b8ac" strokeWidth="1" />
          {[-1, 1].map((k) => (
            <g key={k}>
              <ellipse cx={50 + k * 7.5} cy="50" rx="2.8" ry="3.6" fill="#3a2a3a" />
              <circle cx={50 + k * 7.5 - 0.9} cy="48.7" r="1" fill="#fff" />
              <ellipse cx={50 + k * 12} cy="58" rx="3.6" ry="2.2" fill="#ffb6c8" opacity="0.8" />
            </g>
          ))}
          <path d="M47.6 55.6H52.4L50 58.2Z" fill="#ff8aa8" />
          <path
            d="M47 60Q50 62.4 53 60"
            stroke="#8a6a7a"
            strokeWidth="0.9"
            fill="none"
            strokeLinecap="round"
          />
          <Heart at={[50, 79, 26, 0]} fill={p.heart} />
          <ellipse cx="36" cy="76" rx="5" ry="4" fill="#f1ebe5" stroke="#c4b8ac" strokeWidth="1" />
          <ellipse cx="64" cy="76" rx="5" ry="4" fill="#f1ebe5" stroke="#c4b8ac" strokeWidth="1" />
        </>
      )}
    />
  );
}

/** VIP: a gold crown with gems. */
function Crown() {
  return (
    <Art
      draw={(p) => (
        <>
          <path
            d="M18 72 13 36 32 52 50 26 68 52 87 36 82 72Z"
            fill={p.gold}
            stroke="#b0700f"
            strokeWidth="1.2"
            strokeLinejoin="round"
          />
          <path d="M24 66 21 46 33 56Z" fill="#fff" opacity="0.3" />
          {[
            [13, 36],
            [50, 26],
            [87, 36],
          ].map(([x, y]) => (
            <circle key={x} cx={x} cy={y} r="4.6" fill={p.gold} stroke="#b0700f" strokeWidth="1" />
          ))}
          <rect
            x="16"
            y="70"
            width="68"
            height="13"
            rx="3"
            fill={p.gold}
            stroke="#b0700f"
            strokeWidth="1.2"
          />
          <circle cx="50" cy="76.5" r="4.6" fill="#e2263a" stroke="#fff" strokeWidth="1" />
          <circle cx="32" cy="76.5" r="3.6" fill="#2f7fe0" stroke="#fff" strokeWidth="1" />
          <circle cx="68" cy="76.5" r="3.6" fill="#2f7fe0" stroke="#fff" strokeWidth="1" />
          <path d="M50 44 54 50 50 56 46 50Z" fill="#e2263a" stroke="#fff" strokeWidth="0.8" />
        </>
      )}
    />
  );
}

/** Invite a friend: a profile card with a green "+". */
function FriendCard() {
  return (
    <Art
      draw={() => (
        <>
          <rect x="16" y="28" width="68" height="54" rx="7" fill="#eaf3ff" stroke="#3a8ee6" strokeWidth="3" />
          <rect x="28" y="36" width="44" height="38" rx="4" fill="#a8d0f5" />
          <circle cx="50" cy="50" r="8" fill="#f2c9a0" />
          <path d="M34 74C34 63 66 63 66 74Z" fill="#3a6ec0" />
          <circle cx="20" cy="28" r="10" fill="#43b94d" stroke="#fff" strokeWidth="2.5" />
          <path d="M20 22.5V33.5M14.5 28H25.5" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
        </>
      )}
    />
  );
}

/** The art of a shop tile, by pack code (heart packs) or tile name. */
export function PackArt({ code }: { code: string }) {
  switch (code) {
    case 'hearts_12500':
      return <Barrel />;
    case 'hearts_6000':
      return <Pot />;
    case 'hearts_2200':
      return <HeartSack />;
    case 'hearts_500':
      return <HeartBox />;
    case 'bunny':
      return <Bunny />;
    case 'vip':
      return <Crown />;
    case 'friend':
      return <FriendCard />;
    default:
      return <HeartTrio />;
  }
}

/* ---- league ------------------------------------------------------------------------------------------- */

/** Metal of each league's cup: light, middle, dark. */
const METAL: Record<string, [string, string, string]> = {
  wooden: ['#ffdc8e', '#e4a23e', '#9a5a14'],
  bronze: ['#f6c08e', '#cd7f3e', '#7f4318'],
  silver: ['#ffffff', '#c9d1da', '#7f8b99'],
  gold: ['#fff4b0', '#ffc61a', '#c88a00'],
  platinum: ['#f2fbff', '#a9d9e8', '#5c8fa3'],
  diamond: ['#e6f6ff', '#7fd0ff', '#2c86c9'],
};

const STAR = Array.from({ length: 10 }, (_, i) => {
  const r = i % 2 ? 0.45 : 1;
  const a = (Math.PI / 5) * i - Math.PI / 2;
  return `${(r * Math.cos(a)).toFixed(3)},${(r * Math.sin(a)).toFixed(3)}`;
}).join(' ');

/** Laurel leaves round the cup's foot: [x, y, turn] of each leaf on the left branch. */
const LEAVES = Array.from({ length: 6 }, (_, i) => {
  const a = ((112 + i * 15) * Math.PI) / 180;
  return [40 + 29 * Math.cos(a), 42 + 29 * Math.sin(a), (112 + i * 15 + 90) % 360] as const;
});

/** The league's trophy: a cup with a star on a laurel, in a blue circle. */
export function LeagueCup({ tier }: { tier: string }) {
  const id = useId().replace(/:/g, '');
  const [light, mid, dark] = METAL[tier] ?? METAL.wooden!;
  return (
    <svg viewBox="0 0 80 80" aria-hidden>
      <defs>
        <linearGradient id={`${id}m`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor={mid} />
          <stop offset="0.3" stopColor={light} />
          <stop offset="0.7" stopColor={mid} />
          <stop offset="1" stopColor={dark} />
        </linearGradient>
        <radialGradient id={`${id}b`} cx="0.4" cy="0.35" r="0.7">
          <stop offset="0" stopColor="#5db5f5" />
          <stop offset="1" stopColor="#2f86d6" />
        </radialGradient>
      </defs>
      <circle cx="40" cy="40" r="38" fill={`url(#${id}b)`} />
      {[1, -1].map((k) => (
        <g key={k} transform={k < 0 ? 'translate(80 0) scale(-1 1)' : undefined}>
          <path d="M34 70C20 66 12 54 12 40" stroke="#2f7d32" strokeWidth="1.6" fill="none" />
          {LEAVES.map(([x, y, turn]) => (
            <ellipse
              key={turn}
              cx={x.toFixed(2)}
              cy={y.toFixed(2)}
              rx="5.6"
              ry="2.6"
              transform={`rotate(${turn - 60} ${x.toFixed(2)} ${y.toFixed(2)})`}
              fill="#4caf50"
              stroke="#2f7d32"
              strokeWidth="0.7"
            />
          ))}
        </g>
      ))}
      <g stroke={dark} strokeWidth="0.8">
        <path d="M23 20C12 20 12 36 27 38" stroke={`url(#${id}m)`} strokeWidth="3.4" fill="none" />
        <path d="M57 20C68 20 68 36 53 38" stroke={`url(#${id}m)`} strokeWidth="3.4" fill="none" />
        <path d="M22 16H58V22C58 39 50 47 40 47 30 47 22 39 22 22Z" fill={`url(#${id}m)`} />
        <ellipse cx="40" cy="16" rx="18" ry="3" fill={light} />
        <path d="M37 47H43L44 55H36Z" fill={`url(#${id}m)`} />
        <path d="M31 62 34 55H46L49 62Z" fill={`url(#${id}m)`} />
        <rect x="27" y="61" width="26" height="6.5" rx="1.6" fill={dark} />
      </g>
      <polygon
        points={STAR}
        transform="translate(40 30) scale(8)"
        fill="#fff6b0"
        stroke="#d39a1a"
        strokeWidth="0.1"
      />
      <path
        d="M26 21C26 31 29 38 33 42"
        stroke="#fff"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.45"
        fill="none"
      />
    </svg>
  );
}

/** A booster's picture: a star with "x2", lips with a green arrow up, or a star with "+5". */
export function BoosterArt({ code }: { code: string }) {
  const id = useId().replace(/:/g, '');
  const star = (
    <polygon
      points={STAR}
      transform="translate(18 19) scale(14)"
      fill={`url(#${id}s)`}
      stroke="#e09a12"
      strokeWidth="0.06"
      strokeLinejoin="round"
    />
  );
  const label = (text: string) => (
    <text
      x="27"
      y="34"
      textAnchor="middle"
      fontSize="13"
      fontWeight="900"
      fontStyle="italic"
      fill="#33a845"
      stroke="#fff"
      strokeWidth="2.4"
      paintOrder="stroke"
    >
      {text}
    </text>
  );
  return (
    <svg className="booster-svg" viewBox="0 0 40 40" aria-hidden>
      <defs>
        <linearGradient id={`${id}s`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fff09a" />
          <stop offset="1" stopColor="#f5b21e" />
        </linearGradient>
        <linearGradient id={`${id}l`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ff5a6a" />
          <stop offset="1" stopColor="#c8102e" />
        </linearGradient>
      </defs>
      {code === 'limit' ? (
        <>
          <path d="M5 25C9 17 15 18 20 21 25 18 31 17 35 25 28 26.5 12 26.5 5 25Z" fill={`url(#${id}l)`} />
          <path d="M5 25C12 26.5 28 26.5 35 25 31 34 9 34 5 25Z" fill="#c8102e" />
          <path
            d="M11 29C16 31 24 31 29 29"
            stroke="#fff"
            strokeWidth="1.2"
            opacity="0.4"
            strokeLinecap="round"
            fill="none"
          />
          <path
            d="M20 2 29.5 12H24.5V19H15.5V12H10.5Z"
            fill="#43b94d"
            stroke="#fff"
            strokeWidth="1.2"
            strokeLinejoin="round"
          />
        </>
      ) : (
        <>
          {star}
          {label(code === 'x2' ? 'x2' : '+5')}
        </>
      )}
    </svg>
  );
}
